import { useNavigate } from "react-router-dom";
import { Layout, Button, SectionLabel, SectionHeading, PageHero } from "../components.jsx";
import { navy, blue } from "../data";

const DELIVERABLES = [
  "Full business workflow review",
  "AI opportunity map across your operations",
  "Prioritised automation roadmap",
  "ROI estimate for your top 3 opportunities",
  "Implementation recommendations and next steps",
];

const WHO_FOR = [
  "Business owners exploring AI for the first time",
  "Operations teams looking to identify inefficiencies",
  "Companies with 10+ employees and defined workflows",
  "Businesses handling high customer or operational volume",
  "Founders wanting a clear picture before committing to a project",
];

const BENEFITS = [
  { icon:"🎯", title:"Clarity Before Commitment", desc:"Understand exactly where AI can create value before investing in a full implementation." },
  { icon:"💰", title:"ROI-Focused Output",         desc:"Every opportunity identified includes an estimate of the time, cost, or revenue impact." },
  { icon:"🗺️", title:"A Concrete Roadmap",         desc:"You leave with a prioritised plan — not a vague summary or a sales pitch." },
  { icon:"🤝", title:"No Obligation",               desc:"The Assessment is a standalone engagement. There's no pressure to proceed further." },
];

export default function AIAssessment() {
  const navigate = useNavigate();

  return (
    <Layout>
      <PageHero
        label="Start Here"
        title="AI Opportunity Assessment"
        subtitle="The best first step for any business exploring AI is understanding exactly where it creates value. Our Assessment gives you a clear, structured picture — before committing to a full implementation."
      />

      {/* ── OUTCOME STATEMENT ───────────────────────────────────────────────── */}
      <section className="py-16 bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-lg leading-relaxed" style={{ color: navy }}>
            Most businesses uncover{" "}
            <span className="font-bold text-blue-600">3–5 automation opportunities</span>{" "}
            that can save significant time, improve lead responsiveness, and streamline operations — often within weeks, not months.
          </p>
        </div>
      </section>

      {/* ── WHAT YOU RECEIVE ────────────────────────────────────────────────── */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div>
              <SectionLabel>What's Included</SectionLabel>
              <SectionHeading>What You Receive</SectionHeading>
              <p className="text-gray-500 mt-4 mb-8 leading-relaxed">A comprehensive review of your business operations, delivered as a structured report with clear, actionable recommendations.</p>
              <div className="space-y-4">
                {DELIVERABLES.map((item, i) => (
                  <div key={item} className="flex items-start gap-4">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold text-white" style={{ background: blue }}>{i + 1}</div>
                    <p className="text-gray-700 text-sm font-medium leading-relaxed pt-1">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-lg p-8 border-2 border-blue-100" style={{ background: "#f0f7ff" }}>
              <SectionLabel>Investment</SectionLabel>
              <p className="text-4xl font-bold mb-2" style={{ color: navy }}>$1,500 – $5,000</p>
              <p className="text-gray-500 text-sm mb-8">Depending on business size and operational complexity</p>
              <div className="space-y-3 mb-8">
                {["Delivered within 5–10 business days","Includes a 60-minute debrief call","Written report with clear next steps","No ongoing commitment required"].map(item => (
                  <div key={item} className="flex items-center gap-2">
                    <span className="text-blue-500">✓</span>
                    <span className="text-gray-700 text-sm">{item}</span>
                  </div>
                ))}
              </div>
              <Button className="w-full justify-center" onClick={() => navigate("/contact")}>Book Your Assessment</Button>
            </div>
          </div>
        </div>
      </section>

      {/* ── BENEFITS ────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <SectionLabel>Why Start Here</SectionLabel>
            <SectionHeading>What Makes the Assessment Valuable</SectionHeading>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {BENEFITS.map(b => (
              <div key={b.title} className="bg-gray-50 rounded-lg p-7 border border-gray-100 hover:border-blue-200 hover:shadow-md transition-all duration-200">
                <div className="text-3xl mb-4">{b.icon}</div>
                <h3 className="font-bold text-base mb-2" style={{ color: navy }}>{b.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHO IT'S FOR ────────────────────────────────────────────────────── */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-14">
            <SectionLabel>Is It Right For You?</SectionLabel>
            <SectionHeading>Who the Assessment Is For</SectionHeading>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {WHO_FOR.map(item => (
              <div key={item} className="flex items-start gap-4 p-5 rounded-lg bg-white border border-gray-100 hover:border-blue-200 transition-all duration-200">
                <span className="text-blue-500 flex-shrink-0 mt-0.5 font-bold">✓</span>
                <p className="text-gray-700 text-sm font-medium leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHAT BUSINESSES DISCOVER ────────────────────────────────────────── */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <SectionLabel>Typical Findings</SectionLabel>
            <SectionHeading>What Businesses Usually Discover</SectionHeading>
            <p className="text-gray-500 mt-4 max-w-xl mx-auto text-base leading-relaxed">
              Most companies are surprised by how many automation opportunities already exist in their current operations. The goal of the Assessment is to identify the highest-ROI opportunities before implementing any AI systems.
            </p>
          </div>
          <div className="rounded-lg p-10 border border-blue-100" style={{ background: "#f0f7ff" }}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {[
                { icon: "🎯", text: "3–5 high-value automation opportunities that can be implemented quickly" },
                { icon: "⏱️", text: "10–20 hours of manual admin work per week that can be fully eliminated" },
                { icon: "⚡", text: "Faster response times to leads and customers with automated follow-up" },
                { icon: "📊", text: "Clearer visibility into operations and reporting with real-time dashboards" },
                { icon: "🔗", text: "Opportunities to integrate AI into existing business systems and tools" },
                { icon: "💰", text: "A clear ROI picture before committing to any implementation investment" },
              ].map(item => (
                <div key={item.text} className="flex items-start gap-4 p-4 rounded-lg bg-white border border-blue-100">
                  <span className="text-2xl flex-shrink-0">{item.icon}</span>
                  <p className="text-gray-700 text-sm leading-relaxed font-medium">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────────────────────────────── */}
      <section className="py-24" style={{ background: `linear-gradient(135deg, ${navy} 0%, #1a3260 100%)` }}>
        <div className="max-w-3xl mx-auto px-6 text-center">
          <SectionLabel>Book Your Assessment</SectionLabel>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Find Out Where AI Can Help?</h2>
          <p className="text-gray-300 text-base mb-10 max-w-xl mx-auto leading-relaxed">
            Get in touch and we'll confirm your Assessment, align on scope, and schedule your debrief call. The process is straightforward and starts with a simple conversation.
          </p>
          <Button onClick={() => navigate("/contact")}>Book Your AI Assessment</Button>
        </div>
      </section>

    </Layout>
  );
}